import React, {useEffect, useMemo, useState} from 'react';
import { useSelector } from 'react-redux';
import {
  HiCalendar,
  HiChevronDown,
  HiChevronRight,
  HiClock,
  HiExclamationCircle,
  HiIdentification,
  HiInformationCircle,
  HiLink,
  HiPencilAlt,
  HiPlus,
  HiPuzzle,
  HiTrash
} from 'react-icons/hi';
import SimulationTaskModal from './SimulationTaskModal';
import {animate, stagger} from 'motion';

interface SimulationTask {
  task_id: string;
  task_title: string;
  description: string;
  duration: number;
  skill_required: string;
  dependency: string[];
  gpt_update_type: 'add' | 'update' | 'delete' | 'dependency';
  updated_fields?: string[];
  start_date?: string;
}

interface SimulationApprovalProps {
  interruptId: string;
  workItemId: string;
  plan: SimulationTask[];
  message: string;
  onApprove: () => void;
  onModify: (modifications: any) => void;
  onCancel: () => void;
  loading?: boolean;
  showBigChatBox?: boolean;
  readOnly?: boolean;
}

const SimulationApprovalUI: React.FC<SimulationApprovalProps> = ({
  interruptId,
  workItemId,
  plan,
  message,
  onApprove,
  onModify,
  onCancel,
  loading = false,
  showBigChatBox = false,
  readOnly = false
}) => {
  const isSharedChat = useSelector((state: any) => state.chatMessages.isSharedChat);
  const [selectedTasks, setSelectedTasks] = useState<string[]>(plan.map(task => task.task_id));
  // inline edit controls are revealed on hover per card; no global edit mode
  const [localPlan, setLocalPlan] = useState<SimulationTask[]>(plan);
  const [hasChanges, setHasChanges] = useState(false);
  const [editingTask, setEditingTask] = useState<SimulationTask | null>(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [originalTaskData, setOriginalTaskData] = useState<SimulationTask | null>(null);  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({});
  const [hoveredTask, setHoveredTask] = useState<string | null>(null);
  const [expandedDescriptions, setExpandedDescriptions] = useState<Record<string, boolean>>({});
  const [isCollapsed, setIsCollapsed] = useState(readOnly);

  const handleTaskToggle = (taskId: string) => {
    if (readOnly) return;
    setSelectedTasks(prev => prev.includes(taskId) ? prev.filter(id => id !== taskId) : [...prev, taskId]);
  };

  const toggleCardExpanded = (taskId: string) => {
    setExpandedCards(prev => {
      const isCurrentlyExpanded = prev[taskId];
      if (isCurrentlyExpanded) {
        setExpandedDescriptions(prevDesc => ({ ...prevDesc, [taskId]: false }));
      }
      return { ...prev, [taskId]: !isCurrentlyExpanded };
    });
  };

  const toggleDescriptionExpanded = (taskId: string) => {
    setExpandedDescriptions(prev => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const toggleCollapse = () => {
    setIsCollapsed(prev => !prev);
  };

  useEffect(() => {
    if (isCollapsed) {
      setExpandedCards({});
      setExpandedDescriptions({});
    }
  }, [isCollapsed]);

  // Subtle entrance animation for list cards using Motion One
  useEffect(() => {
    try {
      (animate as any)(
        '.cp-sim-card',
        { opacity: [0, 1], transform: ['translateY(6px)', 'translateY(0px)'] },
        { duration: 0.35, delay: (stagger as any)(0.04), easing: 'ease-out' }
      );
    } catch (e) {
      // non-fatal; motion is optional
    }
  }, [localPlan.length]);

  const startEditingTask = (task: SimulationTask) => {
    if (readOnly) return;
    // Store original task data
    setOriginalTaskData(task);
    
    // Convert simulation task to format expected by TaskTableModal
    const modalTask = {
      ...task,
      item_id: task.task_id,
      id: task.task_id,
      name: task.task_title,
      start_date: task.start_date || undefined,
      dependency: task.dependency || [],
      duration: task.duration || 1,
      skill_required: task.skill_required || 'DEFAULT',
      description: task.description || '',
      completion: 0,
      responsible: '',
      bid_price: 0,
      billing_price: 0,
      additional_resources: [],
      due_date: ''
    };
    
    setEditingTask(modalTask);
    setShowTaskModal(true);
  };

  const handleTaskModalClose = () => {
    setShowTaskModal(false);
    setEditingTask(null);
    setOriginalTaskData(null);
  };

  const handleTaskUpdate = (updatedTaskData?: any) => {
    if (!editingTask || !updatedTaskData) {
      handleTaskModalClose();
      return;
    }

    const normalized: SimulationTask = {
      task_id: editingTask.task_id,
      task_title: updatedTaskData.task_title ?? editingTask.task_title,
      description: updatedTaskData.description ?? editingTask.description,
      duration: updatedTaskData.duration ?? editingTask.duration ?? 1,
      skill_required: updatedTaskData.skill_required ?? editingTask.skill_required ?? 'DEFAULT',
      start_date: updatedTaskData.start_date ?? editingTask.start_date,
      dependency: updatedTaskData.dependency ?? updatedTaskData.dependencies ?? editingTask.dependency ?? [],
      gpt_update_type: 'add', 
    };

    const existsIdx = localPlan.findIndex(t => t.task_id === editingTask.task_id);
    let nextPlan: SimulationTask[] = [];
    if (existsIdx === -1) {
      nextPlan = [...localPlan, { ...normalized, gpt_update_type: 'add' }];
    } else {
      const prev = localPlan[existsIdx];
      const keepType: any = prev.gpt_update_type === 'add' ? 'add' : 'update';
      const updated: SimulationTask = { ...prev, ...normalized, gpt_update_type: keepType } as any;
      nextPlan = localPlan.map((t, i) => (i === existsIdx ? updated : t));
    }
    setLocalPlan(nextPlan);
    // setSelectedTasks(prev => Array.from(new Set([...prev, normalized.task_id]))); 
    setHasChanges(true);
    handleTaskModalClose();
  };

  const addNewTask = () => {
    if (readOnly) return;
    const newTaskId = `sim-${localPlan.length + 1}`;
    const draftTask: SimulationTask = {
      task_id: newTaskId,
      task_title: 'New Task',
      description: '',
      duration: 1,
      skill_required: 'DEFAULT',
      dependency: [],
      gpt_update_type: 'add',
      start_date: undefined
    };
    setEditingTask(draftTask);
    setShowTaskModal(true);
  };

  const removeTask = (taskId: string) => {
    if (readOnly) return;
    const updatedPlan = localPlan.filter(task => task.task_id !== taskId);
    setLocalPlan(updatedPlan);
    setHasChanges(true);
  };

  const handleApprove = () => {
    if (readOnly) return;
    setIsCollapsed(true);
    if (hasChanges) {
      onModify({ plan: localPlan });
    } else {
      // No changes, just approve original selected tasks
      onApprove();
    }
  };

  const handleCancel = () => {
    if (readOnly) return;
    setIsCollapsed(true);
    onCancel();
  };

 
  const headerCancelBtnClass = showBigChatBox
    ? 'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-md font-medium text-xs whitespace-nowrap transition-all duration-150 bg-transparent border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
    : 'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-md font-medium text-xs whitespace-nowrap transition-all duration-150 bg-gray-600 hover:bg-gray-700 text-white';

  const headerApproveBtnClass = showBigChatBox
    ? 'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-md font-medium text-xs whitespace-nowrap transition-all duration-150 bg-blue-600/90 hover:bg-blue-600 text-white shadow-sm'
    : 'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-md font-medium text-xs whitespace-nowrap transition-all duration-150 bg-blue-600 hover:bg-blue-700 text-white shadow-sm';

  // Header summary: show number of tasks and counts by type in collapsed state
  const summaryCounts = useMemo(() => {
    const acc = { add: 0, update: 0, delete: 0, dependency: 0 } as Record<string, number>;
    (localPlan || []).forEach(t => { acc[t.gpt_update_type] = (acc[t.gpt_update_type] || 0) + 1; });
    return acc;
  }, [localPlan]);

  const displayMessage = readOnly ? 'Plan Summary' : message;

  return (
    <div className={`bg-white dark:bg-tertiary-dark border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm mb-3 transition-all duration-200`}>
      <div className={`flex items-center ${showBigChatBox ? 'px-4 py-3' : 'px-3 py-2'} gap-3 border-b border-gray-200 dark:border-gray-800`}>
        <HiExclamationCircle className={`${showBigChatBox ? 'w-6 h-6' : 'w-5 h-5'} text-amber-500 flex-shrink-0`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Simulation Plan</h3>
              <div className="text-xs text-gray-600 dark:text-gray-300 truncate">
                {displayMessage}
              </div>
            </div>
            <div className="flex items-center gap-2 ml-4 shrink-0">
              {!readOnly && !isSharedChat ? (
                <>
                  <button
                    onClick={handleCancel}
                    disabled={loading}
                    className={headerCancelBtnClass}
                    title="Cancel"
                  >
                    {/* <HiX className="w-4 h-4" /> */}
                    <span className="hidden sm:inline">Cancel</span>
                  </button>
                  <button
                    onClick={handleApprove}
                    disabled={loading || localPlan.length === 0}
                    className={headerApproveBtnClass}
                    title="Approve"
                  >
                    {/* <HiCheck className="w-4 h-4" /> */}
                    <span className="hidden sm:inline">Approve</span>
                    {showBigChatBox && (
                      <span className="hidden sm:inline"> ({localPlan.length})</span>
                    )}
                  </button>
                </>
              ) : (
                <button
                  onClick={toggleCollapse}
                  className="inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-md font-medium text-xs whitespace-nowrap transition-all duration-150 bg-transparent  text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                  title={isCollapsed ? 'Expand' : 'Collapse'}
                >
                  {isCollapsed ? <HiChevronDown className="w-4 h-4" /> : <HiChevronRight className="w-4 h-4 rotate-90" />}
                
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {isCollapsed && readOnly && (
        <div className="px-4 py-2 text-xs text-gray-600 dark:text-gray-400">
          {localPlan.length} task{localPlan.length !== 1 ? 's' : ''} in plan
        </div>
      )}

      {!isCollapsed && (
        <div className="mb-2">
          <div className="flex items-center px-3 sm:px-4 justify-between mb-2">
            {/* <h4 className={`${showBigChatBox ? 'text-sm' : 'text-xs'} font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap`}>Proposed Changes ({localPlan.length})</h4> */}
            <div className="flex gap-1.5">

            </div>
          </div>
          <div className="space-y-1.5 px-2.5 max-h-[60vh] overflow-y-auto">
          {localPlan.map((task) => {
            const descText = String(task.description || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
            const descPreview = descText.length > 180 ? `${descText.slice(0, 180).trimEnd()}…` : descText;
            const isDescriptionLong = descText.length > 180;
            const isDescriptionExpanded = expandedDescriptions[task.task_id];
            return (
              <div 
                key={task.task_id}
                className={`group relative border rounded-lg transition-all hover:shadow-sm border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-secondary-dark cp-sim-card `}
                onMouseEnter={() => setHoveredTask(task.task_id)}
                onMouseLeave={() => setHoveredTask(null)}
              >
                <div className="p-2.5">
                  <div className="flex items-start gap-2.5">
                    {/* <input
                      type="checkbox"
                      checked={selectedTasks.includes(task.task_id)}
                      onChange={() => handleTaskToggle(task.task_id)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 self-start mt-1"
                    /> */}
                    <div className="flex-1 min-w-0 pr-12">
                      <button onClick={() => toggleCardExpanded(task.task_id)} className="w-full text-left">
                        <div className="space-y-1">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium flex-shrink-0 ${
                              task.gpt_update_type === 'add' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                              task.gpt_update_type === 'update' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' :
                              task.gpt_update_type === 'delete' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :
                              'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300'
                            }`}>
                              {(task.gpt_update_type || 'unknown').toUpperCase()}
                            </span>
                            <h5 className="font-medium text-gray-900 dark:text-gray-100 text-[13px] leading-tight truncate min-w-0 flex-1">
                              {task.task_title}
                              {showBigChatBox && (
                                <span className="text-gray-500 dark:text-gray-400 font-normal"> ({task.task_id})</span>
                              )}
                            </h5>
                          </div>
                        </div>
                      </button>

                      <div className={`transition-all duration-300 ease-in-out overflow-hidden ${expandedCards[task.task_id] ? 'max-h-[600px] opacity-100 mt-1' : 'max-h-0 opacity-0'}`}>
                        {descText && (
                          <div className="mb-2">
                            <div className={`text-[13px] text-gray-600 dark:text-gray-400 ${isDescriptionExpanded && isDescriptionLong ? 'max-h-[200px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent' : ''}`}>
                              <span className="transition-all duration-300 ease-in-out">
                                {isDescriptionExpanded || !isDescriptionLong ? descText : descPreview}
                              </span>
                            </div>
                            {isDescriptionLong && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleDescriptionExpanded(task.task_id);
                                }}
                                className="mt-1 text-[11px] font-medium text-blue-600 dark:text-blue-400 hover:underline hover:text-blue-700 dark:hover:text-blue-300 transition-colors duration-150 inline-flex items-center gap-0.5"
                              >
                                {isDescriptionExpanded ? 'Show less' : 'Show more'}
                                
                              </button>
                            )}
                          </div>
                        )}
                        <div className="grid grid-cols-1 gap-x-4 gap-y-1 pr-1">
                          {task.task_id && (
                            <div className="text-xs text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                              <HiIdentification className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                              <span>ID:</span> <span className="font-medium font-mono">{task.task_id}</span>
                            </div>
                          )}
                          {task.skill_required && (
                            <div className="text-xs text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                              <HiPuzzle className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                              <span>Skill:</span> <span className="font-medium truncate">{task.skill_required}</span>
                            </div>
                          )}
                          {typeof task.duration === 'number' && task.duration > 0 && (
                            <div className="text-xs text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                              <HiClock className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                              <span>Duration:</span> <span className="font-medium">{task.duration} day{task.duration === 1 ? '' : 's'}</span>
                            </div>
                          )}
                          {task.start_date && (
                            <div className="text-xs text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                              <HiCalendar className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                              <span>Start:</span> <span className="font-medium">{task.start_date}</span>
                            </div>
                          )}
                          {task.dependency && Array.isArray(task.dependency) && task.dependency.length > 0 && (
                            <div className="text-xs text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                              <HiLink className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                              <span>Dependencies:</span> <span className="font-medium truncate">{task.dependency.join(', ')}</span>
                            </div>
                          )}
                          {task.updated_fields && Array.isArray(task.updated_fields) && task.updated_fields.length > 0 && (
                            <div className="text-xs text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                              <HiPencilAlt className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                              <span>Updated:</span> <span className="font-medium">{task.updated_fields.join(', ')}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => toggleCardExpanded(task.task_id)}
                      className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded-md text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                      title={expandedCards[task.task_id] ? 'Collapse details' : 'Expand details'}
                    >
                      {expandedCards[task.task_id] ? <HiChevronDown className="w-4 h-4" /> : <HiChevronRight className="w-4 h-4" />}
                    </button>

                    {/* Edit button - show per-task on hover only */}
                    {/* <button
                      onClick={() => startEditingTask(task)}
                      className={`absolute right-7 top-1/2 -translate-y-1/2 p-1 rounded-md text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-opacity ${hoveredTask === task.task_id ? 'opacity-100' : 'opacity-0'}`}
                      title="Edit task"
                    >
                      <HiPencil className="w-4 h-4" />
                    </button> */}
                    {!readOnly && (
                      <button
                        onClick={() => removeTask(task.task_id)}
                        className={`absolute right-7 top-1/2 -translate-y-1/2 p-1 rounded-md text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 transition-opacity ${hoveredTask === task.task_id ? 'opacity-100' : 'opacity-0'}`}
                        title="Delete task"
                      >
                        <HiTrash className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/*{!readOnly && (*/}
          {/*  <button*/}
          {/*    type="button"*/}
          {/*    onClick={addNewTask}*/}
          {/*    className="w-full border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-2 sm:p-3 text-left hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors duration-150 group"*/}
          {/*    title="Add task"*/}
          {/*  >*/}
          {/*    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">*/}
          {/*      <div className="w-7 h-7 rounded-md border-2 border-dashed border-current flex items-center justify-center text-blue-600 dark:text-blue-400 bg-white/70 dark:bg-transparent">*/}
          {/*        <HiPlus className="w-4 h-4" />*/}
          {/*      </div>*/}
          {/*      <div className="flex-1 min-w-0">*/}
          {/*        <div className={`${showBigChatBox ? 'text-sm' : 'text-xs'} font-medium text-gray-800 dark:text-gray-200 group-hover:text-blue-700 dark:group-hover:text-blue-300`}>Add task</div>*/}
          {/*        <div className="text-[11px] text-gray-500 dark:text-gray-400">Click to create a new simulation task</div>*/}
          {/*      </div>*/}
          {/*    </div>*/}
          {/*  </button>*/}
          {/*)}*/}
          </div>
        </div>
      )}

      {/* {selectedTasks.length === 0 && localPlan.length > 0 && (
        <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded text-xs sm:text-sm text-yellow-800 dark:text-yellow-200 flex items-center gap-2">
          <HiExclamation className="w-4 h-4 flex-shrink-0" />
          <span>No tasks selected. Please select at least one task to proceed with the simulation.</span>
        </div>
      )} */}

      {hasChanges && !readOnly && !isCollapsed && (
        <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded text-xs sm:text-sm text-blue-800 dark:text-blue-200 flex items-center gap-2">
          <HiInformationCircle className="w-4 h-4 flex-shrink-0" />
          <span>You have made changes to the plan. The button will save and execute your modifications.</span>
        </div>
      )}

      {/* Simulation Task Modal for editing */}
      {editingTask && !readOnly && (
        <SimulationTaskModal
          selectedTask={editingTask}
          showModal={showTaskModal}
          isVisiable={showTaskModal}
          onClose={handleTaskModalClose}
          onTaskUpdate={handleTaskUpdate}
          tasks={localPlan}
          skills={[]}
          members={[]}
        />
      )}
    </div>
  );
};

export default SimulationApprovalUI;